import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, ArrowUpRight } from "lucide-react";
import { pct } from "../analytics/AnalyticsShared";

const KIND = {
  FACT: "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/30",
  INSIGHT: "bg-primary/10 text-primary border-primary/30",
};
const STATUS = {
  TRIGGERED: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30",
  NOT_TRIGGERED: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  INSUFFICIENT_DATA: "bg-slate-500/10 text-slate-500 dark:text-slate-400 border-dashed border-slate-500/40",
};
const SEVERITY = { WATCH: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30", ATTENTION: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30" };

export const Tag = ({ children, cls, testId }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-mono-tech uppercase tracking-wider ${cls}`} data-testid={testId}>{children}</span>
);
export const KindTag = ({ kind, testId }) => <Tag cls={KIND[kind]} testId={testId}>{kind}</Tag>;
export const StatusTag = ({ status, testId }) => <Tag cls={STATUS[status]} testId={testId}>{status.replace(/_/g, " ")}</Tag>;

export const fmtValue = (v, unit) => {
  if (v == null) return "—";
  if (unit === "rate") return pct(v);
  if (unit === "hours") return v >= 48 ? `${(v / 24).toFixed(1)} d` : `${v} h`;
  return Number(v).toLocaleString("en-IN");
};

const unitFor = (path) => (path.includes("rate") || path.includes("yield") ? "rate" : path.startsWith("production.") ? "hours" : "count");

export const FactRow = ({ fact }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs py-1.5 border-t border-border/60 first:border-0" data-testid={`brain-fact-${fact.id}`}>
    <KindTag kind="FACT" />
    <span className="font-mono-tech text-muted-foreground">{fact.window}</span>
    <span className="font-medium">{fact.label ? `${fact.metric_path} (${fact.label})` : fact.metric_path}</span>
    <span className="ml-auto font-mono-tech">{fmtValue(fact.value, unitFor(fact.metric_path))}<span className="text-muted-foreground"> · n={fact.sample}</span></span>
  </div>
);

const EvidenceRows = ({ rows }) => {
  const navigate = useNavigate();
  return (
    <div className="mt-2 space-y-1" data-testid="brain-insight-evidence">
      {rows.map((r, i) => (
        <div key={i} className="text-xs font-mono-tech text-muted-foreground flex flex-wrap items-center gap-x-3">
          {Object.entries(r).map(([k, v]) => <span key={k}><span className="opacity-70">{k.replace(/_/g, " ")}:</span> {String(v)}</span>)}
          {r.order_number && (
            <button onClick={() => navigate(`/admin?tab=orders&order=${encodeURIComponent(r.order_number)}`)} className="inline-flex items-center gap-0.5 text-primary hover:underline" data-testid={`brain-order-link-${r.order_number}`}>
              Open order<ArrowUpRight className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export const InsightCard = ({ insight, facts }) => {
  const [open, setOpen] = useState(false);
  const id = insight.id.replace(/[^a-z0-9_-]/gi, "-").toLowerCase();
  const triggered = insight.triggered_by.map((fid) => facts[fid]).filter(Boolean);
  const cell = (label, val, testId) => (
    <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-tech">{label}</p><p className="text-sm font-medium mt-0.5" data-testid={testId}>{val}</p></div>
  );
  return (
    <div className={`p-4 rounded-xl border bg-card ${insight.status === "TRIGGERED" ? "border-orange-500/40" : "border-border"}`} data-testid={`brain-insight-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <KindTag kind="INSIGHT" />
        <StatusTag status={insight.status} testId={`brain-insight-status-${id}`} />
        {insight.severity && <Tag cls={SEVERITY[insight.severity]} testId={`brain-insight-severity-${id}`}>{insight.severity}</Tag>}
        <span className="ml-auto text-xs font-mono-tech text-muted-foreground">{insight.rule}</span>
      </div>
      <p className="mt-3 text-sm" data-testid={`brain-insight-statement-${id}`}>{insight.statement}</p>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cell("Actual", insight.actual ? `${fmtValue(insight.actual.value, insight.unit)} · n=${insight.actual.sample}` : "—", `brain-insight-actual-${id}`)}
        {cell("Baseline", insight.baseline ? `${fmtValue(insight.baseline.value, insight.unit)} · n=${insight.baseline.sample}` : "—", `brain-insight-baseline-${id}`)}
        {cell("Threshold", insight.threshold, `brain-insight-threshold-${id}`)}
        {cell("Sample / window", insight.actual ? `${insight.actual.window} vs ${insight.baseline?.window || "—"} · min n=${insight.min_sample}` : `min n=${insight.min_sample}`, `brain-insight-window-${id}`)}
      </div>
      <button onClick={() => setOpen(!open)} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`brain-insight-toggle-${id}`}>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}Evidence · triggered by {insight.triggered_by.length} metric(s)
      </button>
      {open && (
        <div className="mt-2 p-3 rounded-lg bg-muted/40" data-testid={`brain-insight-details-${id}`}>
          {triggered.length === 0 && <p className="text-xs text-muted-foreground">No metric facts attached.</p>}
          {triggered.map((f) => <FactRow key={f.id} fact={f} />)}
          {insight.evidence?.length > 0 && <EvidenceRows rows={insight.evidence} />}
        </div>
      )}
    </div>
  );
};
